import { FieldId, WitsConcept } from "Backend";
import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import RequestStatus from "redux/slices/RequestStatus";
import { distinct, stripNull } from "helpers/coll";
import {
  AiChatView,
  AiChatWithMessages,
  CalcCaseProperty,
  CaseDisplayGroup,
  CellAiConfigView,
  CustomCaseProperty,
  CustomDocumentProperty,
  CustomPropertyCategory,
  FieldAiConfigView,
  FieldResponseStats,
  FieldTagConfigView,
  Form,
  FormField,
  GetLensFullStateQuery,
  Lens,
  LensChronologyConfigView,
  LensDocumentStatusNode,
  LensOutcomeStatusConfigurationView,
  LensRule,
  LensRuleViewDiff,
  PageLabel,
  PageLabelRule,
  PageLabelStats,
  ProfileGenTestCaseView,
  ReviewConfig,
  RuleCategory,
  SimpleLensView,
  SubAiChatTestProfileGenSubscription,
  SystemPageLabelGroupView,
  TagColor,
  TagView,
  TestCaseStatus,
  ValueGroupValidationError,
} from "generated/graphql";
import { USER_LOGOUT } from "redux/system.action";
import {
  abortDraft,
  addCell,
  addFormField,
  addFormSection,
  addNewRule,
  beginDraft,
  commitDraft,
  createAndAttachReviewConfig,
  createCaseDisplayGroup,
  createChronologyConfig,
  deleteCaseDisplayGroup,
  deleteCell,
  deleteChronologyConfig,
  deleteField,
  deleteOutcomeStatusConfig,
  deleteProfileGenTestCase,
  deleteReviewConfig,
  deleteSection,
  editAllSelectedRules,
  fetchAiChatMessages,
  fetchAiChats,
  fetchCaseDisplayGroups,
  fetchCellAiConfigs,
  fetchChronologyConfig,
  fetchConcepts,
  fetchCustomProperties,
  fetchDraftDiff,
  fetchFieldAiConfigs,
  fetchFieldTagConfigs,
  fetchFormResponseErrors,
  fetchFormResponseStats,
  fetchLensAndRules,
  fetchLensDocumentStatus,
  fetchLensEditorLenses,
  fetchPageLabels,
  fetchPageLabelStats,
  fetchProfileGenTestCases,
  fetchReviewConfigs,
  queryAiChats,
  fetchTags,
  renamePropSelectOption,
  setCaseInsightsEnabled,
  setHidden,
  setSystemPageLabelGroupsForLens,
  updateAnalyticsExclude,
  updateCaseDisplayGroups,
  updateCaseDisplayGroupWithItems,
  updateCell,
  updateCellAiConfig,
  updateCells,
  updateChronologyConfig,
  updateDraft,
  updateFieldAiConfig,
  updateFormField,
  updateFormFields,
  updateFormSections,
  updateLensInfo,
  updateLensRule,
  updateLive,
  updateProfileGenTestCase,
  updateReviewConfig,
  updateRule,
  updateSectionBasedReviewEnabled,
  upsertCalcProperty,
  upsertCaseProperty,
  upsertDocumentProperty,
  upsertFieldTagConfig,
  upsertOutcomeStatusConfig,
  upsertTag,
} from "./lensEditor.action";
import {
  GenerateProfileResponseSampleEvent,
  GenerateProfileResponseStatusEvent,
} from "routes/lens-editor/EventInterfaces";

export type TestProfileGenQueryResult = SubAiChatTestProfileGenSubscription["aiChatTestProfileGen"];

export interface LensRuleTypeComponents {
  category: RuleCategory;
  subtypes?: string[];
  descriptions?: string[];
}

export interface ProfgenBatchCaseState {
  clientCaseId?: string;
  fieldStates: Record<
    // Key is field id stringified
    string,
    {
      events: GenerateProfileResponseSampleEvent[];
      status: undefined | "sample" | "llm" | "end";
    }
  >;
}

// Key is case id stringified
export type ProfgenBatchState = Record<string, ProfgenBatchCaseState>;

export interface ProfileGenTestCaseViewParsed extends ProfileGenTestCaseView {
  labels: string[];
  descriptionText: string;
}

export interface LensEditorState {
  loaded: boolean;
  lenses: SimpleLensView[];
  lens: Lens;
  ruleIdSerial: number;
  form: Form;
  liveRules: LensRule[];
  draftRules: LensRule[];
  draftDiff: LensRuleViewDiff[] | null | undefined;
  selectedCategory: RuleCategory;
  selectedSubtypes: string[];
  selectedField: FormField | undefined;
  selectedDescriptions: string[];
  conceptCache: Record<string, WitsConcept | null>;
  selectedRuleIds: number[];
  toolTab: "detection" | "diff" | "search" | "selection";
  browserConcepts: WitsConcept[];
  status: RequestStatus;
  formResponseErrorsLoading: RequestStatus;
  formResponseErrors: ValueGroupValidationError[];
  formResponseStats: FieldResponseStats[];
  reviewConfigs: ReviewConfig[];
  chronologyConfig?: LensChronologyConfigView;
  pageLabels: PageLabel[];
  pageLabelRules: PageLabelRule[];
  pageLabelsStatus: RequestStatus;
  pageLabelsStats: PageLabelStats[];
  pageLabelsStatsStatus: RequestStatus;
  systemPageLabelGroups: SystemPageLabelGroupView[];
  allSystemPageLabelGroups: SystemPageLabelGroupView[];
  caseProperties: CustomCaseProperty[];
  documentProperties: CustomDocumentProperty[];
  calcProperties: CalcCaseProperty[];
  caseDisplayGroups: CaseDisplayGroup[];
  customPropertiesStatus: RequestStatus;
  aiChats: AiChatView[];
  aiChatWithMessages: AiChatWithMessages | undefined;
  profgenBatches: Record<string, ProfgenBatchState>;
  fieldAiConfigs: FieldAiConfigView[];
  fieldTagConfigs: FieldTagConfigView[];
  cellAiConfigs: CellAiConfigView[];
  lensOutcomeStatusConfigurations: LensOutcomeStatusConfigurationView[];
  profileBuilderAiMode: boolean;
  profileGenTestCases: Record<FieldId, ProfileGenTestCaseViewParsed[]>;
  profileGenTestResults: TestProfileGenQueryResult[];
  lensDocumentStatus: LensDocumentStatusNode;
  tags: TagView[];
  tagColors: TagColor[];
}

export const initialState: LensEditorState = {
  loaded: false,
  lenses: [],
  lens: {} as Lens,
  ruleIdSerial: 0,
  form: {} as Form,
  liveRules: [],
  draftRules: [],
  draftDiff: [],
  selectedCategory: RuleCategory.Product,
  selectedSubtypes: [],
  selectedField: undefined,
  selectedDescriptions: [],
  conceptCache: {},
  selectedRuleIds: [],
  toolTab: "detection",
  browserConcepts: [],
  reviewConfigs: [],
  formResponseErrorsLoading: RequestStatus.IDLE,
  formResponseErrors: [],
  formResponseStats: [],
  status: RequestStatus.IDLE,
  pageLabels: [],
  pageLabelRules: [],
  pageLabelsStatus: RequestStatus.IDLE,
  pageLabelsStats: [],
  pageLabelsStatsStatus: RequestStatus.IDLE,
  systemPageLabelGroups: [],
  allSystemPageLabelGroups: [],
  caseProperties: [],
  documentProperties: [],
  calcProperties: [],
  caseDisplayGroups: [],
  customPropertiesStatus: RequestStatus.IDLE,
  aiChats: [],
  aiChatWithMessages: undefined,
  profgenBatches: {},
  fieldAiConfigs: [],
  fieldTagConfigs: [],
  cellAiConfigs: [],
  lensOutcomeStatusConfigurations: [],
  profileBuilderAiMode: false,
  profileGenTestCases: [],
  profileGenTestResults: [],
  lensDocumentStatus: {} as LensDocumentStatusNode,
  tags: [],
  tagColors: [],
};

export enum TestCaseLabels {
  Starred = "starred",
  NotOnPage = "not_on_page",
  Failing = "failing",
  BadFinding = "bad_finding",
}

export const TEST_CASE_DESCRIPTION_LABEL_LOOKUP: { [key in TestCaseLabels]: string } = {
  [TestCaseLabels.Starred]: "Starred",
  [TestCaseLabels.NotOnPage]: " Not on Page",
  [TestCaseLabels.Failing]: "Failing",
  [TestCaseLabels.BadFinding]: "Bad Finding",
};

export function makeProfileGenTestCaseViewParsed(testCase: ProfileGenTestCaseView): ProfileGenTestCaseViewParsed {
  const description = { labels: [], text: "" };
  const testCaseDescription = stripNull(testCase.description) ?? "";

  if (testCaseDescription !== "") {
    try {
      const parsedDescription = JSON.parse(testCaseDescription);
      description.labels = parsedDescription.labels;
      description.text = parsedDescription.text;
    } catch (e) {
      console.error("Failed to parse description for test case", testCase.id);
    }
  }

  return {
    ...testCase,
    labels: description.labels,
    descriptionText: description.text,
  };
}

const fullUpdate = (state: LensEditorState, payload: GetLensFullStateQuery["lens"]) => {
  state.lens = payload.lens;
  state.form = payload.form;
  state.lensOutcomeStatusConfigurations = payload.lensOutcomeStatusConfigurations;
  state.draftRules = payload.draftRules || [];
  state.liveRules = payload.currentRules;
  if (payload.lens.draftVersion) {
    const maxRuleId = state.draftRules ? Math.max(...state.draftRules.map((r) => r.ruleId)) : 1;
    state.ruleIdSerial = maxRuleId;
  } else {
    state.ruleIdSerial = payload.lens.ruleIdSerial;
  }
  state.draftDiff = [];
  state.loaded = true;
};

const lensEditorSlice = createSlice({
  name: "lensEditor",
  initialState,
  reducers: {
    manuallyCreateFieldState(state, action: PayloadAction<{ field: FormField }>) {
      state.form.fields.push(action.payload.field);
    },
    toggleRuleSelected(state, action: PayloadAction<{ ruleId: number }>) {
      const selected = state.selectedRuleIds;
      const ruleId = action.payload.ruleId;
      const idx = selected.indexOf(ruleId);
      if (idx !== -1) {
        selected.splice(idx, 1);
      } else {
        selected.push(ruleId);
        selected.sort();
      }
    },
    addSelectedRules(state, action: PayloadAction<{ ruleIds: number[] }>) {
      state.selectedRuleIds = distinct(state.selectedRuleIds.concat(action.payload.ruleIds));
    },
    updateFormLegacy(state, action: PayloadAction<{ updateFn: (form: Form) => Form }>) {
      state.form = action.payload.updateFn(state.form);
    },
    setSelectedRuleTypeComponents(state, action: PayloadAction<LensRuleTypeComponents>) {
      state.selectedCategory = action.payload.category;
      state.selectedSubtypes = action.payload.subtypes || [];
      state.selectedDescriptions = action.payload.descriptions || [];
    },
    setSelectedField(state, action: PayloadAction<FormField | undefined>) {
      state.selectedCategory = RuleCategory.Product;
      state.selectedSubtypes = [];
      state.selectedDescriptions = [];
      state.selectedField = action.payload;
    },
    clearSelectedRules(state) {
      state.selectedRuleIds = [];
    },
    setToolTab(state, action: PayloadAction<{ toolTab: LensEditorState["toolTab"] }>) {
      state.toolTab = action.payload.toolTab;
    },
    setProfileBuilderAiMode(state, action: PayloadAction<boolean>) {
      state.profileBuilderAiMode = action.payload;
    },
    browseConcepts(state, action: PayloadAction<{ concepts: WitsConcept[] }>) {
      state.toolTab = "search";
      state.browserConcepts = action.payload.concepts;
    },
    clearLensEditor() {
      return { ...initialState };
    },
    addProfgenSampleEvent(state, action: PayloadAction<{ event: GenerateProfileResponseSampleEvent }>) {
      const event = action.payload.event;
      const batchState = state.profgenBatches[event.batchId];
      const caseState = batchState?.[event.caseId.toString()];
      if (caseState) {
        caseState.clientCaseId = event.clientCaseId;
        const fieldState = caseState.fieldStates[event.fieldId];
        if (fieldState) {
          fieldState.events.push(event);
        } else {
          caseState.fieldStates[event.fieldId] = {
            events: [],
            status: undefined,
          };
        }
      }
    },
    addProfgenStatusEvent(state, action: PayloadAction<{ event: GenerateProfileResponseStatusEvent }>) {
      const event = action.payload.event;
      const batchState = state.profgenBatches[event.batchId];
      const caseState = batchState?.[event.caseId.toString()];
      if (caseState) {
        caseState.clientCaseId = event.clientCaseId;
        const fieldState = caseState.fieldStates[event.fieldId.toString()];
        if (fieldState) {
          fieldState.status = event.outcome as "sample" | "llm" | "end" | undefined;
        } else {
          caseState.fieldStates[event.fieldId.toString()] = {
            events: [],
            status: undefined,
          };
        }
      }
    },
    addProfgenBatch(state, action: PayloadAction<{ batchId: string; caseIds: number[]; fieldIds: number[] }>) {
      const event = action.payload;
      const initialCases: [string, ProfgenBatchCaseState][] = event.caseIds.map((caseId) => [
        caseId.toString(),
        {
          clientCaseId: undefined,
          fieldStates: Object.fromEntries(
            event.fieldIds.map((fieldId) => [fieldId.toString(), { events: [], status: "sample" }])
          ),
        },
      ]);
      state.profgenBatches[event.batchId] = Object.fromEntries(initialCases);
    },
    setProfileGenTestResults(state, action: PayloadAction<{ testResults: TestProfileGenQueryResult[] }>) {
      state.profileGenTestResults = action.payload.testResults;
    },
    appendProfileGenTestResults(state, action: PayloadAction<{ testResults: TestProfileGenQueryResult[] }>) {
      state.profileGenTestResults.push(...action.payload.testResults);

      const testLookup: { [testCaseId: number]: ProfileGenTestCaseView } = Object.values(state.profileGenTestCases)
        .flat()
        .reduce((acc, tc) => ({ ...acc, [tc.id]: tc }), {});
      // Optimistically update all the tests based on results so we don't have to refetch
      action.payload.testResults.forEach((result) => {
        const test = testLookup[result.testCaseId];
        if (test !== undefined) {
          const testsForField = state.profileGenTestCases[test.fieldId] || [];
          const found = testsForField.findIndex((tc) => tc.id === test.id);
          if (found > -1) {
            const allPassed = result.responseResults.every((r) => r.passed);
            testsForField[found]!.lastResult = result;
            testsForField[found]!.status = allPassed ? TestCaseStatus.Passing : TestCaseStatus.Failing;
            testsForField[found]!.lastRunAt = new Date().toISOString();
          }
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(USER_LOGOUT, (_state) => {
      return { ...initialState };
    });

    builder
      .addCase(updateLensInfo.fulfilled, (state, action) => {
        state.lens = {
          ...state.lens,
          ...action.payload,
        };
      })
      .addCase(setHidden.fulfilled, (state, action) => {
        state.lens.hidden = action.payload.hidden;
        state.lenses = state.lenses.map((l) => {
          if (l.id === state.lens.id) {
            return { ...l, hidden: action.payload.hidden };
          }
          return l;
        });
      })
      .addCase(setCaseInsightsEnabled.fulfilled, (state, { payload: { calcsEnabled } }) => {
        state.lens.calcsEnabled = calcsEnabled;
      })
      .addCase(fetchChronologyConfig.fulfilled, (state, action) => {
        state.chronologyConfig = action.payload.lens.lensChronologyConfigs[0];
      })
      .addCase(updateChronologyConfig.fulfilled, (state, action) => {
        state.chronologyConfig = action.payload;
      })
      .addCase(createChronologyConfig.fulfilled, (state, action) => {
        state.chronologyConfig = action.payload;
      })
      .addCase(deleteChronologyConfig.fulfilled, (state) => {
        delete state.chronologyConfig;
      })
      .addCase(fetchCustomProperties.rejected, (state) => {
        state.customPropertiesStatus = RequestStatus.FAILED;
      })
      .addCase(fetchCustomProperties.pending, (state) => {
        state.customPropertiesStatus = RequestStatus.PENDING;
      })
      .addCase(fetchCustomProperties.fulfilled, (state, action) => {
        state.caseProperties = action.payload.caseProperties;
        state.documentProperties = action.payload.documentProperties;
        state.calcProperties = action.payload.calcProperties;
        state.caseDisplayGroups = action.payload.caseDisplayGroups;
        state.customPropertiesStatus = RequestStatus.SUCCEEDED;
      })
      .addCase(fetchCaseDisplayGroups.fulfilled, (state, action) => {
        state.caseDisplayGroups = action.payload;
      })
      .addCase(deleteCaseDisplayGroup.fulfilled, (state, action) => {
        const foundIndex = state.caseDisplayGroups.findIndex((cdg) => cdg.id === action.payload.groupId);

        if (foundIndex > -1) {
          state.caseDisplayGroups.splice(foundIndex, 1);
        }
      })
      .addCase(createCaseDisplayGroup.fulfilled, (state, action) => {
        state.caseDisplayGroups.push(action.payload);
      })
      .addCase(updateCaseDisplayGroupWithItems.fulfilled, (state, action) => {
        const foundIndex = state.caseDisplayGroups.findIndex((cdg) => cdg.id === action.payload.id);

        if (foundIndex > -1) {
          state.caseDisplayGroups.splice(foundIndex, 1, action.payload);
        }
      })
      .addCase(updateCaseDisplayGroups.fulfilled, (state, action) => {
        action.payload.groups.forEach((g) => {
          const found = state.caseDisplayGroups.find((cdg) => cdg.id === g.id);

          if (found !== undefined) {
            found.name = g.name;
            found.description = g.description;
            found.rows = g.rows;
            found.columns = g.columns;
          }
        });
      })
      .addCase(upsertCalcProperty.fulfilled, (state, action) => {
        const foundIndex = state.calcProperties.findIndex((cp) => cp.key === action.payload.key);

        if (foundIndex > -1) {
          state.calcProperties[foundIndex] = action.payload;
        } else {
          state.calcProperties.push(action.payload);
        }
      })
      .addCase(upsertCaseProperty.fulfilled, (state, action) => {
        const property = action.payload;
        const foundIndex = state.caseProperties.findIndex((cp) => cp.key === property.key);

        if (foundIndex > -1) {
          state.caseProperties.splice(foundIndex, 1, property);
        } else {
          state.caseProperties.push(property);
        }
      })
      .addCase(renamePropSelectOption.fulfilled, (state, action) => {
        const payload = action.payload;
        const propCategory = payload.propCategory;

        if (propCategory === CustomPropertyCategory.CaseCustomProperty) {
          const foundProp = state.caseProperties.find((cp) => cp.key === payload.key);

          if (foundProp !== undefined) {
            foundProp.selectOptions = foundProp.selectOptions.map((option) =>
              option === payload.originalSelectOptionName ? payload.newSelectOptionName : option
            );
          }
        }

        if (propCategory === CustomPropertyCategory.CaseCalcProperty) {
          const foundProp = state.calcProperties.find((cp) => cp.key === payload.key);

          if (foundProp !== undefined) {
            foundProp.selectOptions = foundProp.selectOptions.map((option) =>
              option === payload.originalSelectOptionName ? payload.newSelectOptionName : option
            );
          }
        }

        if (propCategory === CustomPropertyCategory.DocumentProperty) {
          const foundProp = state.documentProperties.find((cp) => cp.key === payload.key);

          if (foundProp !== undefined) {
            foundProp.selectOptions = foundProp.selectOptions.map((option) =>
              option === payload.originalSelectOptionName ? payload.newSelectOptionName : option
            );
          }
        }
      })
      .addCase(upsertOutcomeStatusConfig.fulfilled, (state, action) => {
        const foundIndex = state.lensOutcomeStatusConfigurations.findIndex((cp) => cp.id === action.payload.id);

        if (foundIndex > -1) {
          state.lensOutcomeStatusConfigurations[foundIndex] = action.payload;
        } else {
          state.lensOutcomeStatusConfigurations.push(action.payload);
        }
      })
      .addCase(deleteOutcomeStatusConfig.fulfilled, (state, action) => {
        state.lensOutcomeStatusConfigurations = state.lensOutcomeStatusConfigurations.filter(
          (cp) => cp.id !== action.payload.id
        );
      })
      .addCase(upsertDocumentProperty.fulfilled, (state, action) => {
        const foundIndex = state.documentProperties.findIndex((cp) => cp.key === action.payload.key);

        if (foundIndex > -1) {
          state.documentProperties.splice(foundIndex, 1, action.payload);
        } else {
          state.documentProperties.push(action.payload);
        }
      })
      .addCase(fetchPageLabels.rejected, (state) => {
        state.pageLabelsStatus = RequestStatus.FAILED;
      })
      .addCase(fetchPageLabels.pending, (state) => {
        state.pageLabelsStatus = RequestStatus.PENDING;
      })
      .addCase(fetchPageLabels.fulfilled, (state, action) => {
        state.pageLabels = action.payload.lens.pageLabels.listPageLabels.map((lpl) => lpl.label);
        state.pageLabelRules = action.payload.lens.pageLabels.listPageLabels.map((lpl) => lpl.rules).flat();
        state.pageLabelsStatus = RequestStatus.SUCCEEDED;
        state.systemPageLabelGroups = action.payload.lens.pageLabels.systemPageLabelGroups;
        state.allSystemPageLabelGroups = action.payload.lens.pageLabels.allSystemPageLabelGroups;
      })
      .addCase(setSystemPageLabelGroupsForLens.fulfilled, (state, action) => {
        state.systemPageLabelGroups = action.payload.labelGroups;
      })
      .addCase(fetchPageLabelStats.rejected, (state) => {
        state.pageLabelsStatsStatus = RequestStatus.FAILED;
      })
      .addCase(fetchPageLabelStats.pending, (state) => {
        state.pageLabelsStatsStatus = RequestStatus.PENDING;
      })
      .addCase(fetchPageLabelStats.fulfilled, (state, action) => {
        state.pageLabelsStatsStatus = RequestStatus.SUCCEEDED;
        state.pageLabelsStats = action.payload;
      })
      .addCase(fetchLensAndRules.fulfilled, (state, action) => {
        state.status = RequestStatus.SUCCEEDED;
        fullUpdate(state, action.payload.lens);
      })
      .addCase(fetchLensAndRules.pending, (state, _action) => {
        state.status = RequestStatus.PENDING;
      })
      .addCase(fetchLensAndRules.rejected, (state, _action) => {
        state.status = RequestStatus.FAILED;
      })
      .addCase(fetchLensEditorLenses.fulfilled, (state, action) => {
        state.lenses = action.payload;
      })
      .addCase(fetchReviewConfigs.fulfilled, (state, action) => {
        state.reviewConfigs = action.payload;
      })
      .addCase(createAndAttachReviewConfig.fulfilled, (state, action) => {
        const reviewConfig = action.payload;
        state.reviewConfigs.push(reviewConfig);
      })
      .addCase(abortDraft.fulfilled, (state, action) => {
        state.status = RequestStatus.SUCCEEDED;
        fullUpdate(state, action.payload.lens);
      })
      .addCase(commitDraft.fulfilled, (state, action) => {
        state.status = RequestStatus.SUCCEEDED;
        fullUpdate(state, action.payload.lens);
      })
      .addCase(fetchDraftDiff.fulfilled, (state, action) => {
        state.status = RequestStatus.SUCCEEDED;
        state.draftDiff = action.payload;
      })
      .addCase(beginDraft.fulfilled, (state, action) => {
        state.status = RequestStatus.SUCCEEDED;
        state.lens.draftVersion = action.payload;
        state.draftRules = state.liveRules;
      })
      .addCase(updateDraft.fulfilled, (state, action) => {
        state.status = RequestStatus.SUCCEEDED;
        // Apply deletes
        action.payload.deletes.forEach((ruleId) => {
          const idx = state.draftRules.findIndex((r) => r.ruleId === ruleId);
          state.draftRules.splice(idx, 1);
        });
        // Apply updates
        action.payload.rules.forEach((newRule) => {
          const idx = state.draftRules.findIndex((r) => r.ruleId === newRule.ruleId);
          if (idx !== -1) {
            state.draftRules[idx] = newRule;
          } else {
            state.draftRules.push(newRule);
          }
        });
      })
      .addCase(addNewRule.fulfilled, (state, action) => {
        state.status = RequestStatus.SUCCEEDED;
        state.ruleIdSerial = action.payload.ruleId;
        state.draftRules.push(action.payload);
        state.selectedField = state.form.fields.find((f) => f.id === action.payload.fieldId);
        state.selectedCategory = action.payload.category;
        state.selectedSubtypes = [action.payload.subtype];
        state.selectedDescriptions = [action.payload.description];
      })
      .addCase(updateRule.fulfilled, (state, action) => {
        state.status = RequestStatus.SUCCEEDED;
        const newRule = action.payload;
        const idx = state.draftRules.findIndex((r) => r.ruleId === newRule.ruleId);
        if (idx === -1) {
          state.draftRules.push(newRule);
        } else {
          state.draftRules[idx] = newRule;
        }
        state.selectedField = state.form.fields.find((f) => f.id === action.payload.fieldId);
        state.selectedCategory = newRule.category;
        state.selectedSubtypes = [newRule.subtype];
        state.selectedDescriptions = [newRule.description];
      })
      .addCase(updateLensRule.fulfilled, (state, action) => {
        state.status = RequestStatus.SUCCEEDED;
        const newRule = action.payload;
        const idx = state.draftRules.findIndex((r) => r.ruleId === newRule.ruleId);
        if (idx === -1) {
          state.draftRules.push(newRule);
        } else {
          state.draftRules[idx] = newRule;
        }
        state.selectedField = state.form.fields.find((f) => f.id === action.payload.fieldId);
        state.selectedCategory = newRule.category;
        state.selectedSubtypes = [newRule.subtype];
        state.selectedDescriptions = [newRule.description];
      })
      .addCase(fetchConcepts.fulfilled, (state, action) => {
        const { desired, found } = action.payload;
        desired.forEach((conceptId) => {
          const conceptOrNull = found.find((c) => c.id === conceptId) || null;
          state.conceptCache[conceptId] = conceptOrNull;
        });
        state.status = RequestStatus.SUCCEEDED;
      })
      .addCase(addFormField.fulfilled, (state, { payload }) => {
        state.form.fields.push(payload.lens.form.createField);
      })
      .addCase(updateFormField.fulfilled, (state, { payload }) => {
        const field = payload.lens.form.updateFields[0];
        if (!field) {
          return;
        }

        const foundIndex = state.form.fields.findIndex((f) => f.id === field.id);

        if (foundIndex !== -1) {
          const foundField = current(state.form.fields[foundIndex]);
          const { outlinePrefix, ...restField } = field;
          state.form.fields[foundIndex] = { ...foundField, ...restField };
        }
      })
      .addCase(updateFormFields.fulfilled, (state, { payload }) => {
        const fields = payload.lens.form.updateFields;

        fields.forEach((field) => {
          const foundIndex = state.form.fields.findIndex((f) => f.id === field.id);

          if (foundIndex !== -1) {
            state.form.fields[foundIndex] = field;
          }
        });
      })
      .addCase(updateCells.fulfilled, (state, { payload }) => {
        const cells = payload.lens.form.updateCells;
        const fieldIndex = state.form.fields.findIndex((f) => f.id === cells[0]?.fieldId);
        const foundField = state.form.fields[fieldIndex];
        if (foundField) {
          cells.forEach((cell) => {
            const foundIndex = foundField.cells.findIndex((c) => c.id === cell.id);
            if (foundIndex !== -1) {
              foundField.cells[foundIndex] = cell;
            }
          });
        }
      })
      .addCase(updateFormSections.fulfilled, (state, { payload }) => {
        const sections = payload.lens.form.updateSections;

        sections.forEach((section) => {
          const foundIndex = state.form.sections.findIndex((s) => s.id === section.id);

          if (foundIndex !== -1) {
            state.form.sections[foundIndex] = section;
          }
        });
      })
      .addCase(deleteField.fulfilled, (state, { payload }) => {
        state.form.fields = state.form.fields.filter((f) => f.id !== payload.fieldId);
      })
      .addCase(updateCell.fulfilled, (state, { payload }) => {
        const cell = payload.lens.form.updateCells[0];

        if (!cell) {
          return;
        }

        const fieldIndex = state.form.fields.findIndex((f) => f.id === cell.fieldId);
        if (fieldIndex !== -1) {
          const field = state.form.fields[fieldIndex];

          if (!field) {
            return;
          }

          const cellIndex = field.cells.findIndex((c) => c.id === cell.id);
          if (cellIndex !== -1) {
            field.cells[cellIndex] = cell;
          }
        }
      })
      .addCase(deleteCell.fulfilled, (state, { payload }) => {
        const fieldIndex = state.form.fields.findIndex((f) => f.id === payload.fieldId);
        if (fieldIndex !== -1) {
          const field = state.form.fields[fieldIndex];

          if (!field) {
            return;
          }

          field.cells = field.cells.filter((c) => c.id !== payload.cellId);
        }
      })
      .addCase(deleteSection.fulfilled, (state, { payload }) => {
        state.form.sections = state.form.sections.filter((s) => s.id !== payload.formSectionId);
      })
      .addCase(updateLive.fulfilled, (state, action) => {
        state.form.live = action.payload;
      })
      .addCase(updateAnalyticsExclude.fulfilled, (state, action) => {
        state.lens.analyticsExclude = action.payload;
      })
      .addCase(updateSectionBasedReviewEnabled.fulfilled, (state, action) => {
        state.lens.sectionBasedReviewEnabled = action.payload;
      })
      .addCase(addFormSection.fulfilled, (state, action) => {
        state.form.sections.push(action.payload.lens.form.createSection);
      })
      .addCase(addCell.fulfilled, (state, action) => {
        const cell = action.payload.lens.form.createCell;
        const found = state.form.fields.find((f) => f.id === cell.fieldId);
        found?.cells.push(cell);
      })
      .addCase(fetchFormResponseStats.fulfilled, (state, action) => {
        state.formResponseStats = action.payload.lens.formResponse.fieldStats;
      })
      .addCase(fetchFormResponseErrors.fulfilled, (state, action) => {
        state.formResponseErrorsLoading = RequestStatus.SUCCEEDED;
        state.formResponseErrors = action.payload.lens.formResponse.validateResponseData;
      })
      .addCase(fetchFormResponseErrors.pending, (state) => {
        state.formResponseErrorsLoading = RequestStatus.PENDING;
        state.formResponseErrors = [];
      })
      .addCase(fetchFormResponseErrors.rejected, (state) => {
        state.formResponseErrorsLoading = RequestStatus.FAILED;
      })
      .addCase(updateReviewConfig.fulfilled, (state, action) => {
        const foundIndex = state.reviewConfigs.findIndex((rc) => rc.id === action.payload.id);
        if (foundIndex !== -1) {
          state.reviewConfigs[foundIndex] = action.payload;
        }
      })
      .addCase(deleteReviewConfig.fulfilled, (state, action) => {
        state.reviewConfigs = state.reviewConfigs.filter((rc) => rc.id !== action.payload.configId);
      })
      .addCase(editAllSelectedRules.fulfilled, (state, action) => {
        // NOTE: this one just dispatches another one (see the thunk, it dispatches updateDraft)
        // Set the selected field, category, subtype, and description to the bulk change values
        if (action.payload.newFieldId !== undefined)
          state.selectedField = state.form.fields.find((f) => f.id === action.payload.newFieldId);
        if (action.payload.newCategory !== undefined) state.selectedCategory = action.payload.newCategory;
        if (action.payload.newSubtype !== undefined) state.selectedSubtypes = [action.payload.newSubtype];
        if (action.payload.newDescription !== undefined) state.selectedDescriptions = [action.payload.newDescription];
      })
      .addCase(fetchAiChats.fulfilled, (state, action) => {
        state.aiChats = action.payload;
      })
      .addCase(queryAiChats.fulfilled, (state, action) => {
        state.aiChats = action.payload;
      })
      .addCase(fetchAiChatMessages.fulfilled, (state, action) => {
        state.aiChatWithMessages = action.payload;
      })
      .addCase(fetchFieldAiConfigs.fulfilled, (state, action) => {
        state.fieldAiConfigs = action.payload.lens.formQueries.fieldAiConfigs;
      })
      .addCase(fetchFieldTagConfigs.fulfilled, (state, action) => {
        state.fieldTagConfigs = action.payload.lens.formQueries.fieldTagConfigs;
      })
      .addCase(fetchCellAiConfigs.fulfilled, (state, action) => {
        state.cellAiConfigs = action.payload.lens.formQueries.cellAiConfigs;
      })
      .addCase(updateCellAiConfig.fulfilled, (state, action) => {
        // add the cell ai config response to the cell ai configs
        const cellAiConfig = action.payload;
        const foundIndex = state.cellAiConfigs.findIndex((c) => c.cellId === cellAiConfig.cellId);
        if (foundIndex !== -1) {
          state.cellAiConfigs[foundIndex] = cellAiConfig;
        } else {
          state.cellAiConfigs.push(cellAiConfig);
        }
      })
      .addCase(updateFieldAiConfig.fulfilled, (state, action) => {
        // add the field ai config response to the field ai configs
        const fieldAiConfig = action.payload;
        const foundIndex = state.fieldAiConfigs.findIndex((c) => c.fieldId === fieldAiConfig.fieldId);
        if (foundIndex !== -1) {
          state.fieldAiConfigs[foundIndex] = fieldAiConfig;
        } else {
          state.fieldAiConfigs.push(fieldAiConfig);
        }
      })
      .addCase(fetchProfileGenTestCases.fulfilled, (state, action) => {
        const parsed: ProfileGenTestCaseViewParsed[] = action.payload.testCases.map(makeProfileGenTestCaseViewParsed);
        state.profileGenTestCases[action.payload.fieldId] = parsed;
      })
      .addCase(updateProfileGenTestCase.fulfilled, (state, action) => {
        const testCases = state.profileGenTestCases[action.payload.fieldId];
        if (testCases !== undefined) {
          const foundIndex = testCases.findIndex((testCase) => testCase.id === action.payload.id);
          const found = testCases[foundIndex];
          if (foundIndex > -1 && found !== undefined) {
            found.expectFailure = action.payload.expectFailure;
            found.expectedResponse = action.payload.expectedResponse;
            found.description = action.payload.description;
            testCases[foundIndex] = makeProfileGenTestCaseViewParsed(found);
          }
        }
      })
      .addCase(deleteProfileGenTestCase.fulfilled, (state, action) => {
        const testCases = state.profileGenTestCases[action.payload.fieldId];
        if (testCases !== undefined) {
          state.profileGenTestCases[action.payload.fieldId] = testCases.filter(
            (testCase) => testCase.id !== action.payload.testCaseId
          );
        }
      })
      .addCase(fetchLensDocumentStatus.fulfilled, (state, action) => {
        state.lensDocumentStatus = action.payload;
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        state.tags = action.payload.tags;
        state.tagColors = action.payload.colors;
      })
      .addCase(upsertFieldTagConfig.fulfilled, (state, action) => {
        const fieldTagConfig = action.payload;
        const foundIndex = state.fieldTagConfigs.findIndex((c) => c.fieldId === fieldTagConfig.fieldId);
        if (foundIndex !== -1) {
          state.fieldTagConfigs[foundIndex] = fieldTagConfig;
        } else {
          state.fieldTagConfigs.push(fieldTagConfig);
        }
      })
      .addCase(upsertTag.fulfilled, (state, action) => {
        const tag = action.payload;
        const foundIndex = state.tags.findIndex((t) => t.systemName === tag.systemName);
        if (foundIndex !== -1) state.tags[foundIndex] = tag;
        else state.tags.push(tag);
      });
  },
});

export default lensEditorSlice;

export const {
  toggleRuleSelected,
  manuallyCreateFieldState,
  updateFormLegacy,
  setSelectedField,
  setSelectedRuleTypeComponents,
  addSelectedRules,
  clearSelectedRules,
  setToolTab,
  setProfileBuilderAiMode,
  browseConcepts,
  clearLensEditor,
  addProfgenSampleEvent,
  addProfgenStatusEvent,
  addProfgenBatch,
  setProfileGenTestResults,
  appendProfileGenTestResults,
} = lensEditorSlice.actions;
